import {combineReducers} from 'redux';
import {createSelector} from 'reselect';
import uuid from 'uuid';
import {
  getData,
  registerPeopleListener as peopleListener,
  registerAgendaListener as agendaListener,
  registerHomepageListener as homepageListener,
  registerPointsListener as pointsListener,
  deregisterHomepageListener as deregHomepageListener,
  deregisterAgendaListener as deregAgendaListener,
  deregisterPeopleListener as deregPeopleListener,
  deregisterPointsListener as deregPointsListener,
} from '../module/API';
import {
  networkStatusSelector
} from './index';
import {getItem} from '../module/LocalStorage';
import config from '../config/core.json';

// *** REDUCERS

const dataReducer = (state = {}, action) => {

  switch(action.type) {

    case 'DATA_FETCHED':
      if ( JSON.stringify(action.data) !== JSON.stringify(state) ) {
        return action.data;
      } else {
        return state;
      }

    case 'PEOPLE_UPDATED':
      return {
        ...state,
        people: action.people,
      };

    case 'AGENDA_UPDATED':
      return {
        ...state,
        agenda: action.agenda,
      };

    case 'HOMEPAGE_UPDATED':
      return {
        ...state,
        homepage: action.homepage,
      };

    case 'POINTS_UPDATED':
      return {
        ...state,
        points: action.points,
      };

    // TEMP ALTERNATIVE TO LETTING API DO ITS THING
    case 'POINTS_CHANGE':
      return {
        ...state,
        points: {
          ...state.points,
          [uuid()]: {
            personId: action.personId,
            change: action.change,
            ts: action.ts,
          }
        }
      };

    case 'SELECT_STAG':
      return {};

    default:
      return state;

  }

};

const loadingReducer = (state = {}, action) => {

  switch(action.type) {

    case 'START_PEOPLE_FETCH':
      if ( !state.people ) {
        return {
          ...state,
          people: true
        };
      } else {
        return state;
      }

    case 'PEOPLE_UPDATED':
      if ( state.people ) {
        return {
          ...state,
          people: false
        };
      } else {
        return state;
      }

    case 'START_AGENDA_FETCH':
      if ( !state.agenda ) {
        return {
          ...state,
          agenda: true
        };
      } else {
        return state;
      }

    case 'AGENDA_UPDATED':
      if ( state.agenda ) {
        return {
          ...state,
          agenda: false
        };
      } else {
        return state;
      }

    case 'START_HOMEPAGE_FETCH':
      if ( !state.homepage ) {
        return {
          ...state,
          homepage: true
        };
      } else {
        return state;
      }

    case 'HOMEPAGE_UPDATED':
      if ( state.homepage ) {
        return {
          ...state,
          homepage: false
        };
      } else {
        return state;
      }

    case 'START_POINTS_FETCH':
      if ( !state.points ) {
        return {
          ...state,
          points: true
        };
      } else {
        return state;
      }

    case 'POINTS_UPDATED':
      if ( state.points ) {
        return {
          ...state,
          points: false
        };
      } else {
        return state;
      }

    case 'API_OFFLINE':
      return {
        ...state,
        people: false,
        agenda: false,
        homepage: false,
        points: false,
      };

    default:
      return state;

  }

};

// *** MAIN REDUCER

export default combineReducers({
  data: dataReducer,
  loading: loadingReducer
});

// *** SELECTORS

export const dataSelector = (state) => state.data || {};
export const peopleSelector = (state) => dataSelector(state).people;
export const agendaSelector = (state) => dataSelector(state).agenda;
export const homepageSelector = (state) => dataSelector(state).homepage;
export const pointsSelector = (state) => dataSelector(state).points;
export const homepageTitleSelector = (state) => dataSelector(state).homepage ? dataSelector(state).homepage.title : "";
export const hasStagDataSelector = (state) => {
  if ( state.data && state.data.homepage && state.data.homepage.title ) {
    return true;
  }
  return false;
};

export const loadingSelector = (state) => state.loading;
export const agendaLoadingSelector = (state) => loadingSelector(state).agenda;
export const peopleLoadingSelector = (state) => loadingSelector(state).people;
export const homepageLoadingSelector = (state) => loadingSelector(state).homepage;
export const pointsLoadingSelector = (state) => loadingSelector(state).points;

// *** SELECTOR UTILITIES

export const agendaDatesSelector = (agenda) => {
  if ( !agenda ) return [];
  return Object.keys(agenda).filter(id => {
    return agenda[id] !== null;
  }).map(id => {
    return agenda[id].date;
  });
};

export const getTotalPointsForPerson = (points, personId) => {
  return Object.keys(points || {}).map(id => {
    return points[id];
  }).reduce((acc, item, arr, index) => {
    if ( item.personId === personId ) {
      acc += item.change;
    }
    return acc;
  }, 0);
};

export const peopleWithPointsSelector = createSelector(
  state => peopleSelector(state),
  state => pointsSelector(state),
  (people = {}, points = {}) => {
    const peoplePoints = {};
    Object.keys(people || {}).forEach(personId => {
      peoplePoints[personId] = {
        ...people[personId],
        totalPoints: getTotalPointsForPerson(points, personId)
      };
    });
    return peoplePoints;
  }
);

// *** ACTIONS

export const fetchData = (stag) => {
  return (dispatch) => {
    return getData(stag).then(data => {
      dispatch({
        type: 'DATA_FETCHED',
        data
      });
    });
  }
};

export const registerPeopleListener = (stag) => {
  let stagData, timeoutId;
  return (dispatch, getState) => {
    // the first time, set a fetching state
    dispatch({
      type: 'START_PEOPLE_FETCH'
    });
    if ( !networkStatusSelector(getState()) ) {
      stagData = getItem(config.localStorageKey + "_" + stag);
      if ( stagData && stagData.length ) {
        stagData = JSON.parse(stagData);
        if ( stagData && stagData.data && stagData.data.people ) {
          dispatch({
            type: 'PEOPLE_UPDATED',
            people: stagData.data.people,
          });
        }
      }
      timeoutId = window.setTimeout(() => {
        dispatch({
          type: 'PEOPLE_UPDATED',
          people: null
        });
      }, config.requestTimeoutLimitMs);
    }
    return peopleListener(people => {
      if ( timeoutId ) window.clearTimeout(timeoutId);
      dispatch({
        type: 'PEOPLE_UPDATED',
        people
      });
    }, stag);
  };
};

export const registerAgendaListener = (stag) => {
  let stagData, timeoutId;
  return (dispatch, getState) => {
    // the first time, set a fetching state
    dispatch({
      type: 'START_AGENDA_FETCH'
    });
    if ( !networkStatusSelector(getState()) ) {
      stagData = getItem(config.localStorageKey + "_" + stag);
      if ( stagData && stagData.length ) {
        stagData = JSON.parse(stagData);
        if ( stagData && stagData.data && stagData.data.agenda ) {
          dispatch({
            type: 'AGENDA_UPDATED',
            agenda: stagData.data.agenda,
          });
        }
      }
      timeoutId = window.setTimeout(() => {
        dispatch({
          type: 'AGENDA_UPDATED',
          agenda: null
        });
      }, config.requestTimeoutLimitMs);
    }
    return agendaListener(agenda => {
      if ( timeoutId ) window.clearTimeout(timeoutId);
      dispatch({
        type: 'AGENDA_UPDATED',
        agenda
      })
    }, stag);
  };
};

export const registerHomepageListener = (stag) => {
  let stagData, timeoutId;
  return (dispatch, getState) => {
    // the first time, set a fetching state
    dispatch({
      type: 'START_HOMEPAGE_FETCH',
    });
    if ( !networkStatusSelector(getState()) ) {
      stagData = getItem(config.localStorageKey + "_" + stag);
      if ( stagData && stagData.length ) {
        stagData = JSON.parse(stagData);
        if ( stagData && stagData.data && stagData.data.homepage ) {
          dispatch({
            type: 'HOMEPAGE_UPDATED',
            homepage: stagData.data.homepage,
          });
        }
      }
      timeoutId = window.setTimeout(() => {
        dispatch({
          type: 'HOMEPAGE_UPDATED',
          homepage: null,
        });
      }, config.requestTimeoutLimitMs);
    }
    return homepageListener(homepage => {
      if ( timeoutId ) window.clearTimeout(timeoutId);
      dispatch({
        type: 'HOMEPAGE_UPDATED',
        homepage,
      })
    }, stag);
  };
};

export const registerPointsListener = (stag) => {
  let stagData, timeoutId;
  return (dispatch, getState) => {
    // the first time, set a fetching state
    dispatch({
      type: 'START_POINTS_FETCH'
    });
    if ( !networkStatusSelector(getState()) ) {
      stagData = getItem(config.localStorageKey + "_" + stag);
      if ( stagData && stagData.length ) {
        stagData = JSON.parse(stagData);
        if ( stagData && stagData.data && stagData.data.points ) {
          dispatch({
            type: 'POINTS_UPDATED',
            points: stagData.data.points,
          });
        }
      }
      timeoutId = window.setTimeout(() => {
        dispatch({
          type: 'POINTS_UPDATED',
          points: null,
        });
      }, config.requestTimeoutLimitMs);
    }
    return pointsListener(points => {
      if ( timeoutId ) window.clearTimeout(timeoutId);
      dispatch({
        type: 'POINTS_UPDATED',
        points
      })
    }, stag);
  };
};

export const deregisterHomepageListener = (fn, stag) => dispatch => deregHomepageListener(fn, stag);
export const deregisterAgendaListener = (fn, stag) => dispatch => deregAgendaListener(fn, stag);
export const deregisterPeopleListener = (fn, stag) => dispatch => deregPeopleListener(fn, stag);
export const deregisterPointsListener = (fn, stag) => dispatch => deregPointsListener(fn, stag);



// WEBPACK FOOTER //
// ./src/reducers/dataReducer.js